import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { getClinicalFormAuditTrail } from "../../../../services/patientService";
import {
  MODAL_BACKDROP,
  MODAL_BODY,
  MODAL_HEADER,
  MODAL_PANEL,
  MODAL_TITLE,
} from "../../../../ui/pageChrome";
import { PATIENT_THEME } from "../../patientTheme";
import { CLINICAL_FORM_STATUS_LABELS } from "../../clinicalFormCatalog";
import { CloseIcon, HistoryIcon } from "../../../../Icons";
import ClinicalFormStatusBadge from "./ClinicalFormStatusBadge";

/** Verb shown at the top of an entry, keyed by the audit action name. */
const ACTION_LABELS = {
  clinical_form_created: "Created the form",
  clinical_form_updated: "Saved changes",
  clinical_form_submitted: "Sent the form to the office",
  clinical_form_reviewed: "Reviewed the form",
  clinical_form_status_changed: "Changed the status",
  clinical_form_signed: "Signed the form",
  clinical_form_printed: "Printed the form",
  clinical_form_deleted: "Deleted the form",
};

const actionLabel = (action) =>
  ACTION_LABELS[action] || action?.replace(/_/g, " ") || "Updated the form";

/** Actions that never carry field changes, so "nothing changed" is not news. */
const CHANGELESS_ACTIONS = new Set(["clinical_form_printed"]);

/**
 * The status column stores the raw enum, which has no business being shown to
 * a reader — they get the same wording as the badge above.
 */
const changeValue = (field, value) =>
  field === "status" ? CLINICAL_FORM_STATUS_LABELS[value] || value : value;

/** Full timestamp, e.g. "Jul 31, 2026, 09:41:14 PM PST". */
const stamp = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return `${new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).format(date)} PST`;
};

const Value = ({ children, tone }) => (
  <span
    className={`inline-block max-w-full break-words rounded px-1.5 py-0.5 text-[11px] ${
      children
        ? tone
        : "italic text-slate-400 bg-slate-50 border border-slate-200"
    }`}
  >
    {children || "empty"}
  </span>
);

Value.propTypes = { children: PropTypes.node, tone: PropTypes.string };

/** Groups an entry's changes under the form section they belong to. */
const groupBySection = (changes) => {
  const groups = new Map();
  changes.forEach((change) => {
    const list = groups.get(change.section) || [];
    list.push(change);
    groups.set(change.section, list);
  });
  return Array.from(groups, ([section, items]) => ({ section, items }));
};

const AuditEntry = ({ entry }) => (
  <li className="relative pl-5">
    <span className="absolute left-0 top-1.5 h-2 w-2 rounded-full bg-indigo-400 ring-4 ring-indigo-50" />
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="text-[12px] font-semibold text-slate-800">
        {entry.userName || "Unknown user"}
      </span>
      {entry.userRole && (
        <span className="inline-flex items-center rounded-full border border-indigo-100 bg-indigo-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-indigo-700">
          {entry.userRole}
        </span>
      )}
      {entry.status && <ClinicalFormStatusBadge status={entry.status} />}
    </div>
    <p className="mt-0.5 text-[11px] text-slate-500">
      {actionLabel(entry.action)} · {stamp(entry.createdAt)}
    </p>

    {entry.changes?.length ? (
      <div className="mt-1.5 space-y-1.5">
        {groupBySection(entry.changes).map(({ section, items }) => (
          <div
            key={section}
            className="rounded-md border border-slate-200 overflow-hidden"
          >
            <div className="bg-slate-50 px-2 py-1 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
              {section}
            </div>
            <ul className="divide-y divide-slate-100">
              {items.map((change) => (
                <li
                  key={change.field}
                  className="flex flex-wrap items-center gap-1.5 px-2 py-1"
                >
                  <span className="text-[11px] font-medium text-slate-700">
                    {change.label}
                  </span>
                  <Value tone="bg-rose-50 text-rose-700 border border-rose-100 line-through">
                    {changeValue(change.field, change.from)}
                  </Value>
                  <span className="text-[11px] text-slate-400">to</span>
                  <Value tone="bg-emerald-50 text-emerald-700 border border-emerald-100">
                    {changeValue(change.field, change.to)}
                  </Value>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    ) : CHANGELESS_ACTIONS.has(entry.action) ? null : (
      <p className="mt-1 text-[11px] italic text-slate-400">
        No field values changed.
      </p>
    )}
  </li>
);

AuditEntry.propTypes = { entry: PropTypes.object.isRequired };

/**
 * Read-only history of a clinical form: who touched it, in which role, which
 * section they were in, and the before/after of every field they changed.
 */
const ClinicalFormAuditModal = ({ open, patientId, form, onClose }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const formId = form?.id;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getClinicalFormAuditTrail(patientId, formId);
      setEntries(res.result?.entries || res.entries || []);
    } catch (err) {
      toast.error(err.message || "Failed to load audit trail");
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [patientId, formId]);

  useEffect(() => {
    if (!open || !formId) return undefined;
    load();
    return undefined;
  }, [open, formId, load]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className={MODAL_BACKDROP} onClick={onClose}>
      <div
        className={`${MODAL_PANEL} max-w-2xl max-h-[85vh] flex flex-col`}
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={`${MODAL_HEADER} ${PATIENT_THEME.modalHead}`}>
          <div className="min-w-0">
            <h3
              className={`${MODAL_TITLE} ${PATIENT_THEME.modalTitle} flex items-center gap-1.5`}
            >
              <HistoryIcon size={14} />
              Audit Trail
            </h3>
            <p className="mt-0.5 truncate text-[11px] text-slate-500">
              {form?.formName}
              {form?.status
                ? ` · ${CLINICAL_FORM_STATUS_LABELS[form.status] || form.status}`
                : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
            aria-label="Close"
          >
            <CloseIcon size={14} />
          </button>
        </div>

        <div className={`${MODAL_BODY} overflow-y-auto`}>
          {loading && (
            <p className="animate-pulse py-6 text-center text-sm text-slate-400">
              Loading audit trail...
            </p>
          )}

          {!loading && !entries.length && (
            <div className="py-8 text-center">
              <HistoryIcon size={24} className="mx-auto mb-2 text-slate-300" />
              <p className="text-sm font-medium text-slate-500">
                No activity recorded yet
              </p>
              <p className="mt-0.5 text-[11px] text-slate-400">
                Entries appear here once the form is saved or reviewed.
              </p>
            </div>
          )}

          {!loading && entries.length > 0 && (
            <ul className="space-y-4 border-l border-slate-200 pl-1">
              {entries.map((entry) => (
                <AuditEntry key={entry.id} entry={entry} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

ClinicalFormAuditModal.propTypes = {
  open: PropTypes.bool,
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  form: PropTypes.object,
  onClose: PropTypes.func,
};

export default ClinicalFormAuditModal;
